/* eslint-disable camelcase */
import React, { useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { FormProvider } from 'react-hook-form';
import DrawerWrapper, { DrawerFooter } from '../DrawerWrapper';
import { useResourceRequest } from '../hooks/incidentResourceRequestHooks';
import { useResourceSites } from '../hooks/resourceSiteHooks';
import { StylishNewButton } from '../../DesignSystems/New/StylishNewButton';
import { useResourceRequestForm } from './hooks/useResourceRequestForm';
import { ResourceRequestCreateDrawerProps } from './utils/ResourceRequestTypes';
import { isStageCompleted } from './utils/ResourceRequestUtils';
import DeleteModal from '../../DesignSystems/DeleteModal';

// Import all section components
import ResourceRequestorSection from './components/ResourceRequestorSection';
import ResourceRequestDetails from './components/ResourceRequestDetails';
import SectionChiefApprovalSection from './components/SectionChiefApprovalSection';
import RESLApprovalSection from './components/RESLApprovalSection';
import LogisticsApprovalSection from './components/LogisticsApprovalSection';
import FinanceApprovalSection from './components/FinanceApprovalSection';
import { useCustomResourcePersistence } from '../hooks/customResourceTypeHooks';

const ResourceRequestCreateDrawer: React.FC<
  ResourceRequestCreateDrawerProps
> = ({ toggle, initialRequirement, requestId, initialRequest }) => {
  const { data: resourceSites } = useResourceSites();
  const { data: request, isLoading } = useResourceRequest(requestId || '');
  const { persistPendingResources } = useCustomResourcePersistence();
  const [showCancelModal, setShowCancelModal] = useState(false);

  const requestData = request || initialRequest;

  const {
    form,
    currentStage,
    isCompleted,
    isSubmitting,
    isCancelling,
    handleSubmit,
    handleApprove,
    handleCancel,
  } = useResourceRequestForm({
    initialRequirement,
    requestData,
  });

  if (isLoading) {
    return (
      <DrawerWrapper toggle={toggle} title="Loading...">
        <div className="d-flex justify-content-center p-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </DrawerWrapper>
    );
  }

  const onSubmit = async () => {
    await persistPendingResources(form.watch('kind'), form.watch('type'));

    await handleSubmit();

    toggle();
  };

  return (
    <DrawerWrapper
      toggle={toggle}
      title={
        requestData
          ? `Edit Resource Request ${requestData?.request_number || ''}`
          : 'Create Resource Request'
      }
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Offcanvas.Body>
            {/* Resource Details - Always shown */}
            <ResourceRequestDetails
              resourceSites={resourceSites || []}
              isCompleted={isCompleted}
              requestData={requestData}
            />

            {/* Approval Sections */}
            {requestData && (
              <>
                {/* Requestor Section (Always shown) */}
                <ResourceRequestorSection
                  requestData={requestData}
                  isCompleted={isCompleted}
                />

                {/* Section Chief Approval */}
                {requestData && (
                  <SectionChiefApprovalSection
                    requestData={requestData}
                    currentStage={currentStage}
                    onApprove={handleApprove}
                    isSubmitting={isSubmitting}
                    isCompleted={isCompleted}
                  />
                )}

                {/* RESL Review Section */}
                {requestData &&
                  isStageCompleted('section_chief', requestData) && (
                    <RESLApprovalSection
                      requestData={requestData}
                      currentStage={currentStage}
                      onApprove={handleApprove}
                      isSubmitting={isSubmitting}
                      isCompleted={isCompleted}
                    />
                  )}

                {/* Logistics Section */}
                {requestData && isStageCompleted('resl', requestData) && (
                  <LogisticsApprovalSection
                    requestData={requestData}
                    currentStage={currentStage}
                    onApprove={handleApprove}
                    isSubmitting={isSubmitting}
                    isCompleted={isCompleted}
                  />
                )}

                {/* Finance Section */}
                {requestData && isStageCompleted('logistics', requestData) && (
                  <FinanceApprovalSection
                    requestData={requestData}
                    currentStage={currentStage}
                    onApprove={handleApprove}
                    isSubmitting={isSubmitting}
                    isCompleted={isCompleted}
                  />
                )}
              </>
            )}
          </Offcanvas.Body>

          <DrawerFooter>
            <div className="d-flex justify-content-between w-100">
              {/* Cancel Request button on the left */}
              {requestData && !isCompleted && (
                <StylishNewButton
                  className="button--tertiary"
                  type="button"
                  onClick={() => setShowCancelModal(true)}
                  disabled={isSubmitting || isCancelling}
                >
                  Cancel Request
                </StylishNewButton>
              )}

              {/* Regular buttons on the right */}
              <div className="button-group ms-auto">
                <StylishNewButton
                  className="button--secondary button--reverse"
                  type="button"
                  onClick={toggle}
                  disabled={isSubmitting}
                >
                  {isCompleted ? 'Close' : 'Cancel'}
                </StylishNewButton>
                {!isCompleted && (
                  <StylishNewButton
                    className="button--primary"
                    type="submit"
                    disabled={
                      isSubmitting || (!requestData && !form.formState.isValid)
                    }
                  >
                    {isSubmitting
                      ? 'Saving...'
                      : requestData
                        ? 'Save Changes'
                        : 'Create Request'}
                  </StylishNewButton>
                )}
              </div>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>

      {/* Cancel Request Confirmation Modal */}
      {showCancelModal && (
        <DeleteModal
          title="Cancel Resource Request"
          onClose={() => setShowCancelModal(false)}
          onConfirmDelete={() => {
            handleCancel();
            setShowCancelModal(false);
          }}
          buttonDisable={isCancelling}
        >
          <p>Are you sure you want to cancel this resource request?</p>
        </DeleteModal>
      )}
    </DrawerWrapper>
  );
};

export default ResourceRequestCreateDrawer;
